import React, { FC, Fragment, useMemo, useRef, useState, useEffect, FocusEventHandler } from 'react';
import {
    Box,
    CircularProgress,
    FormControl,
    IconButton,
    InputAdornment,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PriceListAction } from 'models/action';
import { useTranslation } from 'react-i18next';
import MonetaryValueInput from 'components/monetary-value/monetary-value.component';
import SortableHeaderLabel from 'components/shared/sortable-header-label/sortable-header-label.component';
import { OrderBy } from 'models/search-result';
import { ACTION_PRICE_LIST_SORT_FIELDS } from 'utils/price-list-sort';

type ActionPriceListItemProps = {
    actions: PriceListAction[];
    onSaveActionPrice: (action: PriceListAction) => void;
    onEditActionPrice: (action: PriceListAction) => void;
    onRemoveActionPrice: (action: PriceListAction) => void;
    currency: string;
    addPriceLoading: boolean;
    editPriceLoading: boolean;
    isTioTandvardPricingForLabEnabled: boolean;
    orderBy: OrderBy;
    onSortChange: (field: string) => void;
};

const ROW_HEIGHT = 80;

const ActionPriceListItem: FC<ActionPriceListItemProps> = ({
    actions,
    onSaveActionPrice,
    onEditActionPrice,
    onRemoveActionPrice,
    currency,
    addPriceLoading,
    editPriceLoading,
    isTioTandvardPricingForLabEnabled,
    orderBy,
    onSortChange,
}) => {
    const { t: translate } = useTranslation('lab-settings');
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const descriptionFlexValue = isSmall ? '1 1 100%' : isTioTandvardPricingForLabEnabled ? '1 1 15%' : '1 1 35%';

    const Row: FC<ListChildComponentProps> = ({ index, style }) => {
        const action = actions[index];
        const [savingSpinner, setSavingSpinner] = useState(false);
        const textRef = useRef<HTMLSpanElement>(null);
        const [isOverflowed, setIsOverflowed] = useState(false);

        const normalizeValue = (value: string) => value.trim().replace(',', '.') || undefined;
        const exists = (v: unknown) => v !== null && v !== undefined;
        const handlePriceUpdate = (
            updatedAction: PriceListAction,
            field: 'basePrice' | 'tiotandvardPrice',
            currentValue?: string | number,
        ) => {
            const hasDbRecord = Boolean(updatedAction.action);
            const rawNextValue = field === 'basePrice' ? updatedAction.basePrice : updatedAction.tiotandvardPrice;
            const nextValue = normalizeValue(rawNextValue ?? '');

            const hasBasePrice = field === 'basePrice' ? exists(nextValue) : exists(updatedAction.basePrice);

            const hasTiotandvardPrice =
                field === 'tiotandvardPrice' ? exists(nextValue) : exists(updatedAction.tiotandvardPrice);

            // no change
            if ((!exists(nextValue) && !exists(currentValue)) || nextValue === currentValue?.toString()) {
                return;
            }

            // remove only if DB record exists AND both empty
            if (hasDbRecord && !hasBasePrice && !hasTiotandvardPrice) {
                onRemoveActionPrice(updatedAction);
            }
            // save new record (partial or full)
            else if (!hasDbRecord && (hasBasePrice || hasTiotandvardPrice)) {
                onSaveActionPrice(updatedAction);
            }
            // edit existing record
            else if (hasDbRecord) {
                onEditActionPrice(updatedAction);
            }
            setSavingSpinner(true);
            setTimeout(() => {
                if (!addPriceLoading || !editPriceLoading) {
                    setSavingSpinner(false);
                }
            }, 500);
        };

        const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
            const value = normalizeValue((event.target as HTMLInputElement).value);
            handlePriceUpdate({ ...action, basePrice: value }, 'basePrice', action.basePrice);
        };

        const handleTiotandvardPriceBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
            const value = normalizeValue((event.target as HTMLInputElement).value);
            handlePriceUpdate({ ...action, tiotandvardPrice: value }, 'tiotandvardPrice', action.tiotandvardPrice);
        };

        const formatGroupNames = () => {
            if (action.groups?.length > 0) {
                return action.groups.map((group) => group?.name).join(' | ');
            }
            return '-';
        };

        const text = formatGroupNames();

        const checkOverflow = () => {
            const el = textRef.current;
            if (el) {
                setIsOverflowed(el.scrollWidth > el.clientWidth);
            }
        };

        useEffect(() => {
            checkOverflow();
            window.addEventListener('resize', checkOverflow);
            return () => window.removeEventListener('resize', checkOverflow);
        }, [text]);

        const typography = (
            <Typography
                ref={textRef}
                variant="body2"
                sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: isOverflowed ? 'ellipsis' : 'clip',
                    whiteSpace: 'nowrap',
                }}
            >
                {text}
            </Typography>
        );

        return (
            <Box
                style={style}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: isSmall ? 'wrap' : 'nowrap',
                    gap: isSmall ? 1 : 0,
                    px: 1,
                    py: 0.5,
                    borderBottom: '1px solid rgba(246, 246, 246, 1)',
                }}
            >
                <Box flex={isSmall ? '1 1 100%' : '1 1 10%'} minWidth={80}>
                    <Typography variant="body2">{action.actionCode}</Typography>
                </Box>

                <Box flex={descriptionFlexValue} minWidth={120}>
                    <Typography variant="body2">{action.description}</Typography>
                </Box>

                <Box flex={isSmall ? '1 1 100%' : '1 1 20%'} minWidth={120}>
                    {isOverflowed ? <Tooltip title={text}>{typography}</Tooltip> : typography}
                </Box>

                {!isSmall && (
                    <Box flex="1 1 10%">
                        <Typography variant="body2">{action.category}</Typography>
                    </Box>
                )}

                {isTioTandvardPricingForLabEnabled &&
                    (() => {
                        const tiotandvardDisabled = !action.basePrice && action.basePrice != '0';
                        return (
                            <Box
                                mr={1}
                                flex={isSmall ? '1 1 100%' : '1 1 20%'}
                                minWidth={160}
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                            >
                                <FormControl fullWidth margin="dense">
                                    <MonetaryValueInput
                                        id="base-price"
                                        size="small"
                                        label={translate('common:TIOTANDVARD_PRICE')}
                                        disabled={tiotandvardDisabled}
                                        value={
                                            action.tiotandvardPrice &&
                                            parseFloat(action.tiotandvardPrice || '0')
                                                .toFixed(2)
                                                .replace('.', ',')
                                        }
                                        onBlur={handleTiotandvardPriceBlur}
                                        InputProps={{
                                            endAdornment: savingSpinner ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                <InputAdornment position="end">{currency}</InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                                {tiotandvardDisabled && (
                                    <Tooltip
                                        title={translate('lab-settings:TIOTANDVARD_PRICE_REQUIRES_BASE_PRICE')}
                                        placement="top"
                                        arrow
                                    >
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            color="info"
                                            sx={{ mt: 0.5, flexShrink: 0 }}
                                        />
                                    </Tooltip>
                                )}
                            </Box>
                        );
                    })()}

                {(action.basePrice || action.basePrice == '0') && !isSmall ? (
                    <Box flex="0 0 auto" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                    </Box>
                ) : (
                    // hidden icon to maintain layout
                    <Box flex="0 0 auto" sx={{ visibility: 'hidden' }}>
                        <CheckCircleIcon fontSize="small" />
                    </Box>
                )}

                <Box flex={isSmall ? '1 1 100%' : '1 1 20%'} minWidth={160}>
                    <FormControl fullWidth margin="dense">
                        <MonetaryValueInput
                            id="base-price"
                            size="small"
                            label={translate('common:PRICE')}
                            value={
                                action.basePrice &&
                                parseFloat(action.basePrice || '0')
                                    .toFixed(2)
                                    .replace('.', ',')
                            }
                            onBlur={handleBlur}
                            InputProps={{
                                endAdornment: savingSpinner ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <InputAdornment position="end">{currency}</InputAdornment>
                                ),
                            }}
                        />
                    </FormControl>
                </Box>

                {!isSmall && (
                    <Box flex="0 0 auto" sx={{ mt: 1 }}>
                        {action.basePrice || action.basePrice == '0' ? (
                            <Tooltip
                                title={translate('lab-settings:REMOVE_ACTION_FROM_LIST').toString()}
                                placement="right"
                            >
                                <IconButton onClick={() => onRemoveActionPrice(action)} size="small">
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            // hidden text to maintain layout
                            <Box flex="0 0 auto" sx={{ visibility: 'hidden' }}>
                                Test
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    const MemoizedRow = useMemo(() => Row, [actions]);

    return (
        <Fragment>
            {/* Header row */}
            <Box
                display="flex"
                flexWrap="wrap"
                p={0.5}
                sx={{
                    borderBottom: '2px solid',
                    borderColor: 'focus.light',
                    fontWeight: 'bold',
                }}
            >
                <Box flex={isSmall ? '1 1 100%' : '1 1 10%'} minWidth={80} px={0.5}>
                    <SortableHeaderLabel
                        field={ACTION_PRICE_LIST_SORT_FIELDS.ACTION_CODE}
                        label={translate('actions-materials:CODE')}
                        orderBy={orderBy}
                        onSortChange={onSortChange}
                    />
                </Box>
                <Box flex={descriptionFlexValue} minWidth={120} px={0.5}>
                    <Typography variant="subtitle2">{translate('common:DESCRIPTION')}</Typography>
                </Box>
                <Box flex={isSmall ? '1 1 100%' : '1 1 20%'} minWidth={120} px={0.5}>
                    <SortableHeaderLabel
                        field={ACTION_PRICE_LIST_SORT_FIELDS.GROUPS}
                        label={translate('lab-settings:GROUP')}
                        orderBy={orderBy}
                        onSortChange={onSortChange}
                    />
                </Box>
                {!isSmall && (
                    <>
                        <Box flex="1 1 10%" px={0.5}>
                            <SortableHeaderLabel
                                field={ACTION_PRICE_LIST_SORT_FIELDS.CATEGORY}
                                label={translate('orders:CATEGORY')}
                                orderBy={orderBy}
                                onSortChange={onSortChange}
                            />
                        </Box>
                        {isTioTandvardPricingForLabEnabled && (
                            <Box flex="1 1 20%" px={0.5}>
                                <Typography variant="subtitle2">{translate('common:TIOTANDVARD_PRICE')}</Typography>
                            </Box>
                        )}
                        <Box flex="0 0 2.5%" />
                        <Box flex="1 1 20%" px={0.5}>
                            <Typography variant="subtitle2">{translate('common:BASE_PRICE')}</Typography>
                        </Box>
                        <Box flex="0 0 2.5%" />
                    </>
                )}
            </Box>

            {/* Virtualized list */}
            <List height={600} itemCount={actions.length} itemSize={ROW_HEIGHT} width="100%">
                {MemoizedRow}
            </List>
        </Fragment>
    );
};

export default ActionPriceListItem;
